/**
 * 纯前端Excel解析服务
 * 使用xlsx库在前端解析Excel文件，不依赖后端API
 */

import * as XLSX from 'xlsx';
import { LEG_COST_IMPORT_SCHEMA, COST_TYPE_OPTIONS, CURRENCY_OPTIONS } from '../config/importSchemas';
import { importApi } from '../api/importApi';
import { settlementApi } from '../api/settlementApi';

class FrontendImportService {
    constructor() {
        this.schema = LEG_COST_IMPORT_SCHEMA;
    }

    /**
     * 解析Excel文件
     * @param {File} file - Excel文件对象
     * @returns {Promise<Object>} 解析结果
     */
    async parseExcelFile(file) {
        try {
            // 检查是否启用后端解析
            if (this.schema.useBackendParsing) {
                return await this.parseWithBackend(file);
            }

            // 前端解析逻辑
            // 读取文件
            const arrayBuffer = await this.readFileAsArrayBuffer(file);

            // 解析Excel
            const workbook = XLSX.read(arrayBuffer, {
                type: 'array',
                cellDates: true,
                dateNF: 'yyyy-mm-dd'
            });

            // 获取第一个工作表
            const sheetName = workbook.SheetNames[0];
            if (!sheetName) {
                throw new Error('Excel文件中没有找到工作表');
            }

            const worksheet = workbook.Sheets[sheetName];

            // 转换为JSON数组
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: '',
                blankrows: false
            });

            // 查找标题行
            const { headerRow, startIndex } = this.findHeaderRow(jsonData);
            if (!headerRow) {
                throw new Error('未找到有效的标题行，请检查Excel文件格式');
            }

            // 获取数据行
            const dataRows = jsonData.slice(startIndex + 1);

            // 构建列名映射
            const columnMapping = this.buildColumnMapping(headerRow);

            // 转换数据
            const parsedData = this.convertData(dataRows, columnMapping);

            // 验证数据
            const validationResult = this.validateData(parsedData);

            return {
                success: true,
                data: parsedData,
                warnings: validationResult.warnings,
                errors: validationResult.errors,
                mapping: columnMapping,
                headerRow
            };

        } catch (error) {
            console.error('Excel解析失败:', error);
            return {
                success: false,
                error: error.message || 'Excel解析失败'
            };
        }
    }

    /**
     * 读取文件为ArrayBuffer
     * @param {File} file - 文件对象
     * @returns {Promise<ArrayBuffer>}
     */
    readFileAsArrayBuffer(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('文件读取失败'));
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * 查找标题行
     * @param {Array} jsonData - Excel原始数据
     * @returns {Object} 标题行和起始索引
     */
    findHeaderRow(jsonData) {
        const { max_header_row } = this.schema.excel_config;

        for (let i = 0; i < Math.min(max_header_row, jsonData.length); i++) {
            const row = jsonData[i];

            // 检查是否包含有效列名
            const hasValidColumns = row.some(cell => {
                const cellValue = (cell || '').toString().trim();
                return cellValue && this.schema.fields.some(field =>
                    field.mapping.some(mapping =>
                        cellValue.toLowerCase().includes(mapping.toLowerCase()) ||
                        mapping.toLowerCase().includes(cellValue.toLowerCase())
                    )
                );
            });

            if (hasValidColumns) {
                return { headerRow: row, startIndex: i };
            }
        }

        return { headerRow: null, startIndex: -1 };
    }

    /**
     * 构建列名映射
     * @param {Array} headerRow - 标题行数据
     * @returns {Object} 列名映射对象
     */
    buildColumnMapping(headerRow) {
        const mapping = {};
        const unmappedColumns = [];

        headerRow.forEach((header, index) => {
            const headerValue = (header || '').toString().trim();
            if (!headerValue) return;

            // 查找匹配的字段
            const matchedField = this.schema.fields.find(field => {
                return field.mapping.some(mapping => {
                    const mappingValue = mapping.toLowerCase();
                    const headerLower = headerValue.toLowerCase();

                    // 精确匹配
                    if (mappingValue === headerLower) return true;

                    // 包含匹配
                    if (headerLower.includes(mappingValue) || mappingValue.includes(headerLower)) return true;

                    return false;
                });
            });

            if (matchedField) {
                mapping[matchedField.name] = {
                    columnIndex: index,
                    headerValue,
                    field: matchedField
                };
            } else {
                unmappedColumns.push(headerValue);
            }
        });

        // 检查必填字段
        const missingFields = this.schema.fields
            .filter(field => field.required && !mapping[field.name])
            .map(field => field.label);

        return {
            mapping,
            unmappedColumns,
            missingFields
        };
    }

    /**
     * 转换数据行
     * @param {Array} dataRows - 数据行数组
     * @param {Object} columnMapping - 列名映射
     * @returns {Array} 转换后的数据
     */
    convertData(dataRows, columnMapping) {
        const { mapping } = columnMapping;

        return dataRows
            .filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined))
            .map((row, index) => {
                const record = {
                    id: `import_${Date.now()}_${index}`, // 临时ID
                    _rowIndex: index + 2 // Excel行号（从2开始，因为第1行通常是标题）
                };

                // 转换每个字段
                Object.entries(mapping).forEach(([fieldName, config]) => {
                    const { columnIndex, field } = config;
                    const cellValue = row[columnIndex];

                    record[fieldName] = this.convertFieldValue(cellValue, field, index);
                });

                return record;
            });
    }

    /**
     * 转换单个字段值
     * @param {*} value - 原始值
     * @param {Object} field - 字段配置
     * @param {number} rowIndex - 行索引
     * @returns {*} 转换后的值
     */
    convertFieldValue(value, field, rowIndex) {
        if (value === null || value === undefined || value === '') {
            return field.default || '';
        }

        const stringValue = value.toString().trim();

        switch (field.type) {
            case 'string':
                return stringValue;

            case 'number':
                const numValue = parseFloat(stringValue);
                return isNaN(numValue) ? field.default || 0 : numValue;

            case 'date':
                if (value instanceof Date) {
                    // 如果是Date对象，格式化为YYYY-MM-DD
                    const year = value.getFullYear();
                    const month = String(value.getMonth() + 1).padStart(2, '0');
                    const day = String(value.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }

                // 如果是字符串，尝试解析
                const dateValue = new Date(stringValue);
                if (isNaN(dateValue.getTime())) {
                    return field.default || '';
                }

                return dateValue.toISOString().split('T')[0]; // YYYY-MM-DD

            case 'enum':
                if (field.name === 'cost_type') {
                    // 费用类型映射
                    const mappedType = COST_TYPE_OPTIONS[stringValue];
                    return mappedType || stringValue.toLowerCase().replace(/[^a-z_]/g, '_');
                } else if (field.name === 'currency') {
                    // 币种映射
                    const mappedCurrency = CURRENCY_OPTIONS[stringValue];
                    return mappedCurrency || stringValue.toUpperCase();
                }
                return stringValue;

            default:
                return stringValue;
        }
    }

    /**
     * 验证数据
     * @param {Array} data - 解析后的数据
     * @returns {Object} 验证结果
     */
    validateData(data) {
        const warnings = [];
        const errors = [];

        data.forEach((record, index) => {
            const rowNumber = record._rowIndex;

            // 验证必填字段
            this.schema.fields
                .filter(field => field.required)
                .forEach(field => {
                    const value = record[field.name];
                    if (!value || (typeof value === 'string' && !value.trim())) {
                        errors.push({
                            row: rowNumber,
                            field: field.label,
                            message: `${field.label}不能为空`
                        });
                    }
                });

            // 验证数值字段
            ['amount', 'exchange_rate'].forEach(fieldName => {
                const value = parseFloat(record[fieldName]);
                if (fieldName === 'amount' && !value) {
                    errors.push({
                        row: rowNumber,
                        field: fieldName,
                        message: '金额必须是有效数字'
                    });
                }
                if (fieldName === 'exchange_rate' && value <= 0) {
                    warnings.push({
                        row: rowNumber,
                        field: fieldName,
                        message: '汇率应该大于0'
                    });
                }
            });
        });

        return { warnings, errors };
    }

    /**
     * 下载Excel模板
     */
    async downloadTemplate() {
        try {
            // 调用后端API下载智能模板
            const response = await settlementApi.legCost.downloadTemplate();
            
            // 创建下载链接
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', '航段费用导入模板.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            return { success: true };
        } catch (error) {
            console.error('模板下载失败:', error);
            throw error;
        }
    }

    /**
     * 获取字段示例值
     * @param {Object} field - 字段配置
     * @returns {*} 示例值
     */
    getExampleValue(field) {
        switch (field.name) {
            case 'departure_airport':
                return 'CGO';
            case 'arrival_airport':
                return 'LGG';
            case 'flight_number':
                return 'O3761';
            case 'flight_date':
                return new Date().toISOString().split('T')[0];
            case 'timezone':
                return 'UTC';
            case 'cost_type':
                return 'Landing/Parking';
            case 'supplier':
                return '示例供应商';
            case 'currency':
                return 'USD';
            case 'amount':
                return 1500.00;
            case 'exchange_rate':
                return 1.0;
            default:
                return '';
        }
    }

    /**
     * 使用后端API解析Excel文件
     * @param {File} file - Excel文件对象
     * @returns {Promise<Object>} 解析结果
     */
    async parseWithBackend(file) {
        try {
            // 调用后端解析API
            const response = await importApi.requestParse({
                importType: this.schema.importType,
                dataContent: file,
                inputType: 'file'
            });

            // 处理后端返回的数据格式
            if (response && response.preview_id) {
                const { preview_id, preview_data, warnings } = response;

                // 为每条记录添加临时ID
                const processedData = (preview_data || []).map((item, index) => ({
                    ...item,
                    id: `import_${Date.now()}_${index}`,
                    _rowIndex: index + 2 // Excel行号（从2开始）
                }));

                return {
                    success: true,
                    data: processedData,
                    warnings: warnings || [],
                    errors: [],
                    mapping: {},
                    headerRow: this.schema.fields.map(field => field.label)
                };
            } else {
                throw new Error(response && response.message ? response.message : '后端解析失败');
            }

        } catch (error) {
            console.error('后端Excel解析失败:', error);
            return {
                success: false,
                error: (error.response && error.response.data && error.response.data.detail) || error.message || '后端Excel解析失败'
            };
        }
    }
}

export default FrontendImportService;